import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import {
	BillingFeature,
	PopupContextSwitchAction as ContextSwitchAction,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import definite from 'webapp/helpers/definite';
import setField from 'webapp/helpers/set-field';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';
import FormSelectBoardWithCreateNewOptionComponent, {
	NewBoardId,
} from 'webapp/components/form/select-board-with-create-new-option';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Block from 'webapp/models-custom/block';

export default class BlockPopupDuplicateComponent extends PopupInnerModalComponent<PopupData.Block.Duplicate> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('request') declare requestService: Service.Request;
	@service('billing') declare billingService: Service.Billing;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;

	@tracked blockName = '';
	@tracked selectedBoardId = '';

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		const blockName = this.block.name;
		const baseName = blockName ? `Copy of ${blockName}` : '';
		this.blockName = baseName.length > 50 ? `${baseName.slice(0, 47)}...` : baseName;
		this.selectedBoardId = String(this.block.boardId || '');
	}

	get block() {
		return this.popupData.block;
	}

	@action
	changeSelectedBoardId(boardId: string) {
		this.selectedBoardId = boardId;
	}

	@action
	confirmRedirect(block: Block) {
		this.navigationService.createContextSwitchModal().open({
			actionType: ContextSwitchAction.ADD_TO,
			sourceItem: block,
			targetItem: block.boardObj,
			onSwitch: () => {
				// replace null with board id
				const newRoute = `/layout-designer/${block.boardId}/${block.id}`;
				this.navigationService.replaceRoute(newRoute);
			},
		});
	}

	@action
	confirm() {
		if (!this.selectedBoardId) {
			return;
		}

		// create new datacard / datawall
		if (this.selectedBoardId === NewBoardId) {
			if (this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
				const data = {
					primaryView: this.block.board?.primaryView,
				};

				this.submitInProgress = true;
				const board = this.mmBoardsService.createNewModel(data);
				board
					.save()
					.then((block) => {
						this.analyticsService.event(TrackingEvent.DATABLOCK_DUPLICATE, {
							origin: this.popupData.trackingOrigin,
						});

						next(() => {
							this.cloneTo(block.id!);
						});
					})
					.finally(() => {
						this.submitInProgress = false;
					})
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			}
		} else {
			this.cloneTo(this.selectedBoardId);
		}
	}

	cloneTo(boardId: string) {
		if (this.submitInProgress) {
			return;
		}

		this.submitInProgress = true;

		void this.mmBlocksService
			.duplicateTo(this.block, this.blockName, boardId)
			.then((blockModel) => {
				if (blockModel) {
					this.analyticsService.event(TrackingEvent.DATABLOCK_DUPLICATE, {
						origin: this.popupData.trackingOrigin,
					});

					this.mmBlocksService.checkToMarkBlockVisualizationUsed(blockModel);

					// if board is same close popup, otherwhise open new modal
					if (this.block.boardId === blockModel.boardId) {
						if (!this.navigationService.activatedRoute?.includes('designer')) {
							// when duplicating from block popup preview we show user confirm redirect modal
							this.confirmRedirect(blockModel);
						} else {
							next(() => {
								this.navigationService.transitionToRoute(
									'designer.board.block',
									blockModel.model,
								);
							});
						}
					} else {
						this.confirmRedirect(blockModel);
					}
				}

				if (this.popupData.onDuplicateSuccess) {
					this.popupData.onDuplicateSuccess();
				}

				this.close();
			})
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch();
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Copy" @size="lg" />
			Duplicate datablock "{{this.block.name}}"
		</div>
		<div class="content">
			<div class="row">
				<label for="block-name">Edit datablock name</label>
				<FormInputComponent
					name="block_name"
					id="block-name"
					maxlength="50"
					disabled={{this.submitInProgress}}
					@value={{this.blockName}}
					@onEmpty={{setField target=this property="blockName"}}
					@hideFloatLabel={{true}}
				/>
			</div>
			<p>
				Please select the destination dashboard for the selected datablock to be duplicated
				to:
			</p>

			<FormSelectBoardWithCreateNewOptionComponent
				@changeSelectedBoardId={{this.changeSelectedBoardId}}
				@defaultBoardChoice={{1}}
				@preselectedBoardId={{definite this.block.board.id}}
			/>

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Duplicate"
						@extraClass="save"
						@onTrigger={{this.confirm}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>

		</div>
	</template>
}
